<template>
  <v-combobox
    :model-value="modelValue"
    auto-select-first="exact"
    menu-icon=""
    variant="outlined"
    placeholder="Search for a team member"
    bg-color="bgCanvas"
    rounded="lg"
    base-color="grey400"
    color="grey400"
    hide-details
    hide-no-data
    chips
    return-object
    density="compact"
    closable-chips
    :items="formattedItems"
    no-filter
    @update:search="searchTerm = $event; emit('update:search', $event)"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #item="{ props: itemProps, item }">
      <div class="item" v-bind="itemProps">
        <div class="d-flex flex-column align-items-start justify-content-start">
          <p class="name">
            <span
              v-for="(part, index) in item.raw.parts"
              :key="`${item.raw.id}-${index}`"
              :class="{ highlight: part.highlight }"
            >
              {{ part.text }}
            </span>
          </p>
          <span v-if="item.raw.email" class="email">{{ item.raw.email }}</span>
        </div>
        <DpplNewButton
          v-if="item.raw.type_key === TYPE_LEAD"
          variant="text"
          color="brandPrimary"
          size="small"
        >
          Invite to datapeople
        </DpplNewButton>
      </div>
    </template>
    <template #chip="{ item, props: chipProps }">
      <v-chip
        v-bind="chipProps"
        variant="outlined"
        close-icon="$close"
        :class="{ lead: item.raw.type_key === TYPE_LEAD }"
      >
        <span class="name">{{ item.raw.first_name }} {{ item.raw.last_name }}</span>
      </v-chip>
    </template>
    <template #append-inner>
      <v-progress-circular v-if="loading" color="primary" indeterminate :size="18" :width="2" class="mr-2" />
    </template>
  </v-combobox>
</template>

<script setup>
import { computed, ref } from 'vue';
import { TYPE_LEAD } from '@/helpers/constants/userType';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue', 'update:search']);

const searchTerm = ref('');

const highlightItemMatch = (item) => {
  const name = `${item.first_name} ${item.last_name}`;

  if (!searchTerm.value
    || !name.toLowerCase().includes(searchTerm.value.toLowerCase())) return [{ text: name }];

  const parts = name.split(new RegExp(`(${searchTerm.value})`, 'gi'));

  return parts.map((part) => {
    if (part.toLowerCase() === searchTerm.value.toLowerCase()) {
      return { text: part, highlight: true };
    }
    return { text: part, highlight: false };
  });
};

const formattedItems = computed(() => props.items.map((item) => {
  const highlightedParts = highlightItemMatch(item);

  return {
    ...item,
    parts: highlightedParts,
  };
}));
</script>

<style scoped lang="less">
.v-combobox {
  :deep(.v-field).v-field--appended {
    padding: 0;
  }

  :deep(.v-field__outline) {
    --v-field-border-width: 1px !important;
  }

  :deep(.v-field__input) {
    padding: 0px 16px;

    input {
      font-size: 13px;
      font-weight: 500 !important;
      line-height: 150%;
    }
  }

  :deep(.v-chip) {
    border: @stroke-default 1px solid !important;
    background: @white !important;
    color: @text-darkest;

    &.lead {
      color: @text-brand !important;
    }

    i {
      font-size: 12px;
    }
  }
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  height: 51px;

  &:hover {
    background: @bg-canvas;
    cursor: pointer;
  }

  .email {
    font-size: 11px;
    font-weight: 500;
    line-height: 120%;
    color: @text-light;
    margin-top: 2px;
  }

  .link {
    font-weight: 500;
    font-size: 12px;
    line-height: 120%;
    color: @text-lighter;
    cursor: pointer;
  }
}

.name {
  font-weight: 500;
  font-size: 13px;
  line-height: 150%;
  color: @text-dark;

  .highlight {
    color: @text-brand;
  }
}

:deep(.v-chip.lead) .name {
  color: @text-brand !important;
}
</style>
